import { Field, useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { MultiSelectOption } from '../../models/MultiSelectOption';
import { getQueryParts } from '../../services/mobileAccountService';
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from '../dialog/DialogContext';
import Label from '../label/label';
import SettingSelectField from '../settingFields/SettingSelectField';

interface AccountDetailProps {
    isLicensed: boolean,
    isAccountPopulated: boolean, // Whether all the required details are populated to allow querying
    currentItem: any
}

export const MobileAccountDetails: FC<AccountDetailProps> = ({ isLicensed, isAccountPopulated, currentItem }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [partslist, setPartsList] = useState<MultiSelectOption[]>(new Array(0));
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const { dirty } = useFormikContext<any>();

    useEffect(() => {
        if (partslist.length === 0 &&
            currentItem.details.partNumber &&
            currentItem.details.partNumber.id &&
            currentItem.details.partNumber.value) {
            setPartsList([{
                value: currentItem.details.partNumber.value,
                label: currentItem.details.partNumber.value
            }]);
        }
    }, [currentItem.details.partNumber])

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * 4. The account must be enabled.
    * @returns 
    */
    const canQueryAccount = () => {
        return isLicensed && isAccountPopulated && !dirty && currentItem?.enabled;
    }

    const handleQueryPartsClick = (event) => {
        setBusy(true);
        getQueryParts(values.details.partNumber.value).then(parts => {
            setBusy(false);
            setPartsList(parts);
            values.details.credentialsAvailable.value = parts[0].count;
        }).catch((errorResult) => {
            setBusy(false);
            setPartsList(new Array(0));
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(errorResult.messageKey) });
        })
    };

    return <ActivityFrame busy={busy} className='flex flex-col w-full  bg-white'>
        <div className='flex flex-row'>
            <div className="flex flex-col">
                <Label htmlFor='details.partNumber.value' className="text-xs text-gray-600 mt-1">
                    {translate.byKey(TranslationKeys.partNumber)}:
                </Label>
                <SettingSelectField
                    disabled={!isAccountPopulated}
                    accessor="details.partNumber.value"
                    options={partslist}
                    className='w-56' />
            </div>
            <div className="flex flex-col px-2 pt-3 justify-center">
                <button
                    disabled={!canQueryAccount()}
                    type="button"
                    className={`flex ml-2 px-4 p-1 rounded-lg shadow text-white text-xs items-center  ${!canQueryAccount() ? 'secondary-disabled cursor-not-allowed' : 'bg-sidemenu-focus'}`}
                    onClick={handleQueryPartsClick}>
                    {translate.byKey(TranslationKeys.query)}
                </button>
            </div>
        </div>
        <div className="flex flex-row">
            <div className="flex flex-col w-56">
                <Label htmlFor='details.credentialsAvailable.value'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.credentialsAvailable)}:</Label>
                <Field
                    disabled={true}
                    name="details.credentialsAvailable.value"
                    type="text"
                    className="flex flex-row items-center p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
        </div>
    </ActivityFrame>
};
